import {EventEmitter, Injectable, Output} from '@angular/core';
import * as bcrypt from 'bcryptjs';
import {SecurityApiService} from './security-api.service';
import {AppSettings} from '../../config/app-setting';
import {UserAccessPermissions} from "../entities/UserAccessPermissions";
import {CreatedResult} from "../entities/CreatedResult";
import {GetPermissionResult} from "../entities/GetPermissionResult";
import {MenuService} from "./menu-service";
import { SwalWithDefaults } from 'src/shared/swal-dialog-default-options';

@Injectable({
  providedIn: 'root'
})
export class SecurityService  {

  @Output() loginChangedEventEmitter = new EventEmitter<boolean>();
  @Output() loginPasswordChangedEventEmitter = new EventEmitter<boolean>();

  constructor(private securityApiService: SecurityApiService,
              private menuService: MenuService)
  {
    this.loginChangedEventEmitter.emit(true);
  }

  public async UserLogin(email: string, password: string): Promise<boolean> {
    const encoder = new TextEncoder();
    const passwordUtf8 = encoder.encode(password);
    const hexpassword = new TextDecoder().decode(passwordUtf8);

    const hashedPassword = bcrypt.hashSync(hexpassword, AppSettings.salt);

    try {
      const result = await this.securityApiService.PostLogin(email, hashedPassword);

      if (result.success && !result.error) {
        this.ClearUserAuthentication();
        localStorage.setItem(AppSettings.myTokenKey, result.token);
        localStorage.setItem(AppSettings.myTokenExpirationDate, result.expirationDate.toString());
        localStorage.setItem(AppSettings.myEmail, email.toLowerCase());
        this.loginChangedEventEmitter.emit(true);
        return true;
      } else {
          this.ClearUserAuthentication();
          this.loginChangedEventEmitter.emit(true);
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  private GetUtf8Encoded(myString: string)
  {
    const encoder = new TextEncoder();
    const passwordUtf8 = encoder.encode(myString);

    return new TextDecoder().decode(passwordUtf8);
  }

  private showPasswordChangedMessage(): void {
    SwalWithDefaults.fire({
      title: 'Password modificata con successo!',
      icon: 'success'
    });
  }

  public async UserChangePassword(email: string, oldPassword: string, newPassword: string): Promise<boolean> {
    let success = false;

    const hashedPassword = bcrypt.hashSync(this.GetUtf8Encoded(oldPassword), AppSettings.salt);
    const newHashedPassword = bcrypt.hashSync(this.GetUtf8Encoded(newPassword), AppSettings.salt);

    try {
      const resp = await this.securityApiService.PostLoginChangePassword(email.toLowerCase(), hashedPassword, newHashedPassword);

      if (resp.success && !resp.error && resp.token) {
        this.ClearUserAuthentication();
        localStorage.setItem(AppSettings.myTokenKey, resp.token);
        localStorage.setItem(AppSettings.myTokenExpirationDate, resp.expirationDate.toString());
        localStorage.setItem(AppSettings.myEmail, email.toLowerCase());
        this.showPasswordChangedMessage();
        this.loginPasswordChangedEventEmitter.emit(true);
        success = true;
      }
    } catch (error) {
      success = false;
    }

    return success;
  }

  public async UserResetPassword(email: string, language2LettersCode: string): Promise<boolean> {
    let success = false;

    try {
      const resp = await this.securityApiService.PostResetPassword(email.toLowerCase(), language2LettersCode);
      if (resp.success && !resp.error) {
        success = true;
      }
      
    } catch (error) {
      success = false;
    }

    return success;
  }

  GetUserEmail() : string
  {
    const userEmail = localStorage.getItem(AppSettings.myEmail);
    if (userEmail) {
      return userEmail;
    }

    return "";
  }

  GetTokenExpirationDate()
  {
    let tokenExpirationDate: number = 0;

    const storedTokenExpirationDate = localStorage.getItem(AppSettings.myTokenExpirationDate);
    if (storedTokenExpirationDate) {
      tokenExpirationDate = parseInt(storedTokenExpirationDate);
    }

    return tokenExpirationDate; // NB: Se non esiste in localStorage significa che l'utente non risulta loggato. Restituisce zero in questo caso.
  }

  async UserRefreshToken()
  {
    const result = await this.securityApiService.PostRefreshToken();
    if (result.success && !result.error && result.token !== undefined)
    {
        localStorage.setItem(AppSettings.myTokenKey, result.token);
        localStorage.setItem(AppSettings.myTokenExpirationDate, result.expirationDate.toString());
    }
  }

  public UserLogout()
  {
      this.ClearUserAuthentication();
      this.loginChangedEventEmitter.emit(true);
  };

  public IsUserAuthenticated() : boolean
  {
    let myKey = localStorage.getItem(AppSettings.myTokenKey);
    let myExpirationDate = localStorage.getItem(AppSettings.myTokenExpirationDate);
    if (myExpirationDate !== null) {
      // @ts-ignore
      if ((myExpirationDate - Date.now() > 0)) {
        return (myKey !== null && myKey !== undefined && myKey !== "");
      }
      localStorage.removeItem(AppSettings.myTokenKey);
      localStorage.removeItem(AppSettings.myEmail);
    }
    return false;
  }

  public async PostChangeUserAccessPermission(accessPermission: UserAccessPermissions): Promise<CreatedResult> {
    try {
      const resp = await this.securityApiService.PostChangeUserAccessPermission(accessPermission);
      if (resp.success && !resp.error) {
        return resp;
      }
      else {
        return new CreatedResult();
      }
    }
    catch (error) { }

    return new CreatedResult();
 }

  async GetAllUserAccessPermissions(): Promise<GetPermissionResult> {
    return await this.securityApiService.GetAllUserPermissions();
  }

  async GetUserAccessPermissions(): Promise<GetPermissionResult> {
    return await this.securityApiService.GetApiUserAccessPermissions();
  }

  public SetUserNotLogged()
  {
    localStorage.setItem(AppSettings.myTokenKey, "");
  }

  public ClearUserAuthentication()
  {
    localStorage.removeItem(AppSettings.myTokenKey);
  }

  public async DisableAccount(): Promise<boolean> {
    let userEmail = this.GetUserEmail();
    try {
      const result = await this.securityApiService.PutDisableAccount(userEmail);

      if (result.success) {
        await this.UserRefreshToken();
        return true;
      }
      else {
        console.error('Error disabling account: ' + result.errorMessage);
        return false;
      }
    } catch (error) {
      console.error('Error disabling account: ' + error);
      return false
    }
  }
}
